import axios from 'axios';

const api = axios.create({ baseURL: '', withCredentials: true });

// Redirect to /login on 401 — but NOT for /auth/me (the session check on every
// page load). /auth/me returning 401 is normal for unauthenticated visitors and
// must not redirect the homepage or any public page to /login.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const url: string = (err as { config?: { url?: string } }).config?.url ?? '';
    const status: number | undefined = (err as { response?: { status?: number } }).response?.status;
    const isSessionCheck = url.includes('/auth/me');
    if (status === 401 && !isSessionCheck && window.location.pathname !== '/login') {
      const next = window.location.pathname + window.location.search;
      window.history.replaceState({ next }, '', '/login');
      window.location.replace('/login');
    }
    return Promise.reject(err);
  },
);

// ── Device fingerprint ──────────────────────────────────────────────────────
// A lightweight, stable per-device id used as a multi-account / collusion
// signal. It is NOT a security control — just a soft hint for the house's
// linked-account review. Derived from userAgent + screen + timezone, hashed,
// and cached in localStorage so it stays stable across sessions on one device.

const DEVICE_FP_KEY = 'device_fp';

/** Synchronous 53-bit string hash (cyrb53) — small, stable, no crypto needed. */
function cyrb53(str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const out = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return out.toString(16).padStart(14, '0');
}

/** Return a stable device fingerprint, generating + caching it on first use. */
export function getDeviceFingerprint(): string {
  try {
    const cached = localStorage.getItem(DEVICE_FP_KEY);
    if (cached) return cached;
  } catch {
    // localStorage unavailable (private mode) — fall through to ephemeral id.
  }
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    tz = '';
  }
  const raw = [
    navigator.userAgent || '',
    navigator.language || '',
    `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}x${window.screen?.colorDepth ?? 0}`,
    tz,
  ].join('|');
  const fp = cyrb53(raw);
  try {
    localStorage.setItem(DEVICE_FP_KEY, fp);
  } catch {
    // ignore — return the computed value even if we cannot persist it.
  }
  return fp;
}

/** Headers carrying the device fingerprint, for register/login requests. */
function deviceFpHeader(): Record<string, string> {
  return { 'X-Device-FP': getDeviceFingerprint() };
}

// ── Auth ──────────────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  display_name?: string | null;
  balance_cents?: number;
  kyc_tier: number;
  /** Whether the account has confirmed its email via the 6-digit code. */
  email_verified?: boolean;
  is_verified?: boolean;
  is_pep?: boolean;
}

/** FICA/KYC registration payload — mirrors the backend RegisterRequest. */
export interface RegisterPayload {
  email: string;
  password: string;
  full_name: string;
  id_type: 'sa_id' | 'passport';
  id_number: string;
  /** Required for passport; for sa_id it is derived server-side from the ID. */
  date_of_birth?: string;
  mobile_number: string;
  residential_address: string;
  province?: string;
  /** Required (FICA). */
  occupation: string;
  /** Required (FICA AML). */
  source_of_funds: string;
  accept_terms: boolean;
  acknowledge_responsible_gambling: boolean;
  marketing_opt_in?: boolean;
  /** PEP self-declaration — only collected when sa_compliance_enabled. */
  is_pep?: boolean;
  referral_code?: string | null;
}

export const register = (payload: RegisterPayload) =>
  api.post<User>('/api/v1/auth/register', {
    email: payload.email,
    password: payload.password,
    full_name: payload.full_name,
    id_type: payload.id_type,
    id_number: payload.id_number,
    ...(payload.date_of_birth ? { date_of_birth: payload.date_of_birth } : {}),
    mobile_number: payload.mobile_number,
    residential_address: payload.residential_address,
    ...(payload.province ? { province: payload.province } : {}),
    occupation: payload.occupation,
    source_of_funds: payload.source_of_funds,
    accept_terms: payload.accept_terms,
    acknowledge_responsible_gambling: payload.acknowledge_responsible_gambling,
    marketing_opt_in: payload.marketing_opt_in ?? false,
    is_pep: payload.is_pep ?? false,
    ...(payload.referral_code ? { referral_code: payload.referral_code } : {}),
  }, { headers: deviceFpHeader() });

export const login = (email: string, password: string) =>
  api.post<User>('/api/v1/auth/login', { email, password }, { headers: deviceFpHeader() });

export const logout = () => api.post('/api/v1/auth/logout');

export const getMe = () => api.get<User>('/api/v1/auth/me');

// ── Email verification ──────────────────────────────────────────────────────

export const verifyEmail = (code: string) =>
  api.post<{ email_verified: boolean }>('/api/v1/auth/verify-email', { code });

export const resendVerification = () =>
  api.post<{ sent: boolean; detail: string }>('/api/v1/auth/resend-verification');

// ── Public runtime config ───────────────────────────────────────────────────

export interface PublicConfig {
  sa_compliance_enabled: boolean;
}

export const getPublicConfig = () => api.get<PublicConfig>('/api/v1/public/config');

// ── Responsible-gambling limits ─────────────────────────────────────────────

export interface AccountLimits {
  deposit_limit_daily_cents: number | null;
  deposit_limit_weekly_cents: number | null;
  deposit_limit_monthly_cents: number | null;
  loss_limit_daily_cents: number | null;
  loss_limit_weekly_cents: number | null;
}

export interface SetLimitsResult {
  applied: string[];
  deferred: string[];
}

/** Read the current user's deposit + loss limits (cents; null = unset). */
export const getAccountLimits = () => api.get<AccountLimits>('/api/v1/account/limits');

/**
 * Set deposit and/or loss limits. Decreases (stricter) apply immediately;
 * increases are deferred 24h by the backend. Pass only the keys you want to
 * change, each in cents.
 */
export const setAccountLimits = (body: {
  daily?: number;
  weekly?: number;
  monthly?: number;
  loss_limit_daily?: number;
  loss_limit_weekly?: number;
}) => api.post<SetLimitsResult>('/api/v1/account/limits', body);

// ── Wallet ────────────────────────────────────────────────────────────────

export interface BalanceResponse {
  balance_cents: number;
  currency: string;
}

export const getBalance = () => api.get<BalanceResponse>('/api/v1/wallet/balance');

export interface Transaction {
  id: string;
  amount_cents: number;
  txn_type: 'deposit' | 'withdrawal' | 'bet_debit' | 'bet_win' | 'bet_refund' | 'cashout';
  reference: string | null;
  created_at: string;
}

export interface TransactionsResponse {
  items: Transaction[];
  total: number;
  page: number;
  pages: number;
}

export const getTransactions = (page = 1) =>
  api.get<TransactionsResponse>('/api/v1/wallet/transactions', { params: { page } });

export interface DepositResponse {
  deposit_id: string;
  checkout_url: string;
}

export const initDeposit = (amount_cents: number, success_url: string, cancel_url: string) =>
  api.post<DepositResponse>('/api/v1/wallet/deposit/init', { amount_cents, success_url, cancel_url });

export const withdrawFunds = (amount_cents: number) =>
  api.post('/api/v1/wallet/withdraw', { amount_cents });

// ── Sports & Events ───────────────────────────────────────────────────────

export interface Sport {
  id: string;
  slug: string;
  name: string;
  icon: string;
}

export const getSports = () => api.get<Sport[]>('/api/v1/sports');

export interface EventSummary {
  id: string;
  sport_slug: string;
  league: string;
  home_team: string;
  away_team: string;
  starts_at: string;
  status: 'scheduled' | 'live' | 'finished' | 'suspended' | 'cancelled';
  home_score: number | null;
  away_score: number | null;
  match_time: string | null;
}

export const getEvents = (sport_slug?: string) =>
  api.get<EventSummary[]>('/api/v1/events', { params: sport_slug ? { sport_slug } : {} });

export interface Selection {
  id: string;
  name: string;
  odds: number;
}

export interface Market {
  id: string;
  name: string;
  status: 'open' | 'suspended' | 'closed';
  selections: Selection[];
}

export interface EventDetail extends EventSummary {
  markets: Market[];
}

export const getEventDetail = (event_id: string) =>
  api.get<EventDetail>(`/api/v1/events/${event_id}`);

export const getOdds = (event_id: string) =>
  api.get<Market[]>(`/api/v1/events/${event_id}/odds`);

// ── Betting ───────────────────────────────────────────────────────────────

export interface BetLegRequest {
  selection_id: string;
  market_id: string;
  stake_cents: number;
}

export interface BetResponse {
  id: string;
  bet_type: 'single' | 'accumulator';
  status: 'pending' | 'won' | 'lost' | 'void' | 'cashedout';
  total_stake_cents: number;
  potential_payout_cents: number;
  actual_payout_cents: number | null;
  created_at: string;
  legs: BetLegDetail[];
}

export interface BetLegDetail {
  id: string;
  event_name: string;
  market_name: string;
  selection_name: string;
  odds_at_placement: number;
  result: 'pending' | 'won' | 'lost' | 'void' | null;
}

export const placeBet = (legs: BetLegRequest[], bet_type: 'single' | 'accumulator') =>
  api.post<BetResponse[]>('/api/v1/bets', { legs, bet_type });

export const getMyBets = (status?: string) =>
  api.get<BetResponse[]>('/api/v1/bets/me', { params: status ? { status } : {} });

export const getBetDetail = (id: string) =>
  api.get<BetResponse>(`/api/v1/bets/${id}`);

export interface CashoutPriceResponse {
  cashout_amount_cents: number;
}

export const getCashoutPrice = (id: string) =>
  api.get<CashoutPriceResponse>(`/api/v1/bets/${id}/cashout`);

export const cashout = (id: string) =>
  api.post<BetResponse>(`/api/v1/bets/${id}/cashout`);

// ── AI Predictions ────────────────────────────────────────────────────────

export interface Prediction {
  event_id: string;
  // Backend returns probabilities as floats 0-1 and confidence as a float 0-1.
  home_prob: number;
  draw_prob: number;
  away_prob: number;
  confidence: number;
  explanation: string;
}

/** Map a 0-1 confidence float to a UI band. */
export function confidenceBand(c: number): 'high' | 'medium' | 'low' {
  if (c >= 0.7) return 'high';
  if (c >= 0.4) return 'medium';
  return 'low';
}

export const getPrediction = (event_id: string) =>
  api.get<Prediction>(`/api/v1/events/${event_id}/prediction`);

// ── KYC ───────────────────────────────────────────────────────────────────

export interface KYCStatus {
  tier: number;
  status: string;
}

export const getKYCStatus = () => api.get<KYCStatus>('/api/v1/kyc/status');

export async function submitKYC(payload: { tier_requested: number; document_type: string; document_front_b64: string }) {
  const res = await fetch('/api/v1/kyc/submit', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
  if (!res.ok) throw new Error((await res.json()).detail || 'KYC submission failed')
  return res.json()
}

// ── Admin ─────────────────────────────────────────────────────────────────

function adminApi(adminEmail: string) {
  // The admin token (provisioned from my-investments-admin) is sent as
  // X-Internal-Key; the backend rejects admin calls without a valid token.
  const token = localStorage.getItem('admin_token') ?? '';
  const headers: Record<string, string> = { 'X-Admin-Email': adminEmail };
  if (token) headers['X-Internal-Key'] = token;
  return axios.create({
    baseURL: '',
    withCredentials: true,
    headers,
  });
}

export interface RiskSummary {
  open_liability_cents: number;
  handle_today_cents: number;
  ggr_today_cents: number;
  pending_bets_count: number;
}

export interface MarketExposureItem {
  market_id: string;
  market_name: string;
  event_name: string;
  open_liability_cents: number;
  market_limit_cents: number;
  exposure_pct: number;
}

export interface SharpBettorItem {
  user_id: string;
  email: string;
  win_streak: number;
  total_wagered_cents: number;
}

export interface KYCQueueItem {
  submission_id: string;
  user_id: string;
  email: string;
  tier_requested: string;
  document_type: string;
  submitted_at: string;
}

export interface RiskPage {
  market_exposures: MarketExposureItem[];
  sharp_bettors: SharpBettorItem[];
  kyc_queue: KYCQueueItem[];
}

export interface AdminUserSummary {
  user_id: string;
  email: string;
  kyc_tier: number;
  balance_cents: number;
  is_sharp_bettor: boolean;
  is_self_excluded: boolean;
  created_at: string;
}

export interface AdminUserList {
  items: AdminUserSummary[];
  total: number;
  page: number;
  pages: number;
}

export interface AdminUserDetail {
  user_id: string;
  email: string;
  kyc_tier: number;
  balance_cents: number;
  is_sharp_bettor: boolean;
  is_self_excluded: boolean;
  is_active: boolean;
  created_at: string;
  last_bets: unknown[];
  last_ledger: unknown[];
}

export interface AdminSelectionItem {
  id: string;
  name: string;
  odds_decimal: number;
}

export interface AdminMarketItem {
  market_id: string;
  name: string;
  market_type: string;
  status: string;
  selections: AdminSelectionItem[];
}

export interface AdminEventItem {
  event_id: string;
  home_team: string;
  away_team: string;
  starts_at: string;
  status: string;
  sport_slug: string;
  markets: AdminMarketItem[];
}

export interface DailyReport {
  date: string;
  total_bets: number;
  total_stake_cents: number;
  total_payout_cents: number;
  ggr_cents: number;
  new_users: number;
  active_bettors: number;
}

export const adminGetRiskSummary = (email: string) =>
  adminApi(email).get<RiskSummary>('/api/v1/admin/risk/summary');

export const adminGetRiskPage = (email: string) =>
  adminApi(email).get<RiskPage>('/api/v1/admin/risk');

export const adminReviewKYC = (
  email: string,
  submissionId: string,
  action: 'approve' | 'reject',
  reason?: string,
) =>
  adminApi(email).post(`/api/v1/admin/kyc/${submissionId}/review`, { action, reason });

export const adminListUsers = (email: string, page = 1) =>
  adminApi(email).get<AdminUserList>('/api/v1/admin/users', { params: { page } });

export const adminGetUser = (email: string, userId: string) =>
  adminApi(email).get<AdminUserDetail>(`/api/v1/admin/users/${userId}`);

export const adminRestrictUser = (email: string, userId: string, action: 'restrict' | 'unrestrict') =>
  adminApi(email).post(`/api/v1/admin/users/${userId}/restrict`, { action });

export const adminListEvents = (email: string, sport_slug?: string, search?: string) =>
  adminApi(email).get<AdminEventItem[]>('/api/v1/admin/events', {
    params: { ...(sport_slug ? { sport_slug } : {}), ...(search ? { search } : {}) },
  });

export const adminMarketAction = (
  email: string,
  marketId: string,
  action: 'suspend' | 'resume' | 'settle',
  home_score?: number,
  away_score?: number,
) =>
  adminApi(email).post(`/api/v1/admin/markets/${marketId}/action`, {
    action,
    ...(home_score !== undefined ? { home_score } : {}),
    ...(away_score !== undefined ? { away_score } : {}),
  });

export const adminUpdateOdds = (
  email: string,
  marketId: string,
  selectionId: string,
  odds_decimal: number,
) =>
  adminApi(email).post(`/api/v1/admin/markets/${marketId}/odds`, {
    selection_id: selectionId,
    odds_decimal,
  });

export const adminGetDailyReport = (email: string, date: string) =>
  adminApi(email).get<DailyReport>('/api/v1/admin/reports/daily', { params: { date } });

export const adminPromoteEvent = (email: string, eventId: string) =>
  adminApi(email).post(`/api/v1/admin/events/${eventId}/promote`);

// ── Admin House Risk ────────────────────────────────────────────────────────

export interface HouseConfig {
  house_bankroll_cents: number;
  max_total_liability_pct: number;
  max_stake_cents: number;
  max_payout_cents: number;
  max_market_exposure_cents: number;
  max_acca_payout_cents: number;
  max_user_daily_stake_cents: number;
  live_betting_enabled: boolean;
  dynamic_odds_enabled: boolean;
  dynamic_odds_sensitivity_pct: number;
  dynamic_odds_max_drift_pct: number;
  live_bet_delay_seconds: number;
  live_bet_odds_tolerance_pct: number;
  updated_by: string | null;
  total_open_liability_cents: number;
  capacity_used_pct: number;
}

export type HouseConfigUpdate = Partial<{
  house_bankroll_cents: number;
  max_total_liability_pct: number;
  max_stake_cents: number;
  max_payout_cents: number;
  max_market_exposure_cents: number;
  max_acca_payout_cents: number;
  max_user_daily_stake_cents: number;
  live_betting_enabled: boolean;
  dynamic_odds_enabled: boolean;
  dynamic_odds_sensitivity_pct: number;
  dynamic_odds_max_drift_pct: number;
  live_bet_delay_seconds: number;
  live_bet_odds_tolerance_pct: number;
}>;

export const getHouseConfig = (email: string) =>
  adminApi(email).get<HouseConfig>('/api/v1/admin/house/config');

export const updateHouseConfig = (email: string, body: HouseConfigUpdate) =>
  adminApi(email).put<HouseConfig>('/api/v1/admin/house/config', body);

// ── Admin Linked Accounts (multi-account / collusion review) ─────────────────

export interface LinkedUser {
  id: string;
  email: string;
  created_at: string | null;
  multi_account_flag: boolean;
  is_active: boolean;
}

export interface LinkedGroup {
  /** signup_ip | mobile_number | residential_address | device_fingerprint */
  signal: string;
  value_masked: string;
  users: LinkedUser[];
}

export const getLinkedAccounts = (email: string) =>
  adminApi(email).get<LinkedGroup[]>('/api/v1/admin/risk/linked-accounts');

export const clearMultiFlag = (email: string, userId: string) =>
  adminApi(email).post<{ user_id: string; multi_account_flag: boolean }>(
    `/api/v1/admin/risk/users/${userId}/clear-multi-flag`,
  );

// ── Loyalty & Referral ────────────────────────────────────────────────────

export interface LoyaltyTransaction {
  id: string;
  type: string;
  amount_cents: number;
  currency: string;
  referral_id: string | null;
  bet_id: string | null;
  created_at: string | null;
}

export interface LoyaltyResponse {
  balance_cents: number;
  reward_per_referral: number;
  referral_code: string;
  referral_link: string;
  successful_referrals: number;
  pending_referrals: number;
  cap: number;
  transactions: LoyaltyTransaction[];
}

export const getLoyalty = () => api.get<LoyaltyResponse>('/api/v1/users/me/loyalty');

// ── Partners ──────────────────────────────────────────────────────────────

export type ContactMethod = 'whatsapp' | 'call' | 'email';

export interface PartnerStatusResponse {
  status: 'none' | 'pending' | 'approved' | 'rejected';
  qualifying_bettors: number;
  milestone_target: number;
  contact_method: ContactMethod | null;
  contact_detail: string | null;
  reject_reason: string | null;
  applied_at: string | null;
  decided_at: string | null;
}

export const getPartnerStatus = () =>
  api.get<PartnerStatusResponse>('/api/v1/users/me/partner');

export const applyForPartner = (payload: { contact_method: ContactMethod; contact_detail: string }) =>
  api.post<PartnerStatusResponse>('/api/v1/users/me/partner-application', payload);

// ── Admin Flyers ──────────────────────────────────────────────────────────

export interface AdminFlyerOut {
  id: string;
  event_id: string;
  status: string;
  image_url: string | null;
  generation_method: string;
  created_by: string;
  approved_by: string | null;
  approved_at: string | null;
  sort_order: number;
  created_at: string | null;
}

/** AI-generate a flyer (async — backend returns 202 with a `generating` flyer). */
export const adminCreateEventFlyerAI = (email: string, eventId: string, prompt?: string) =>
  adminApi(email).post<{ flyer_id: string; status: string }>(
    `/api/v1/admin/events/${eventId}/flyers`,
    prompt ? { method: 'ai_generated', prompt } : { method: 'ai_generated' },
  );

/** Synchronously generate a flyer with the PIL renderer (backend returns 201). */
export const adminCreateEventFlyerPIL = (email: string, eventId: string) =>
  adminApi(email).post<AdminFlyerOut>(`/api/v1/admin/events/${eventId}/flyers`, {
    method: 'pil',
  });

export const adminListEventFlyers = (email: string, eventId: string) =>
  adminApi(email).get<AdminFlyerOut[]>(`/api/v1/admin/events/${eventId}/flyers`);

export const adminUpdateEventFlyer = (
  email: string,
  eventId: string,
  flyerId: string,
  body: { status?: 'approved' | 'archived' | 'draft'; sort_order?: number },
) =>
  adminApi(email).patch<AdminFlyerOut>(
    `/api/v1/admin/events/${eventId}/flyers/${flyerId}`,
    body,
  );

export const adminDeleteEventFlyer = (email: string, eventId: string, flyerId: string) =>
  adminApi(email).delete(`/api/v1/admin/events/${eventId}/flyers/${flyerId}`);

export const adminPushEventMarketing = (email: string, eventId: string) =>
  adminApi(email).post(`/api/v1/admin/events/${eventId}/push-marketing`);

// ── Admin Partners ────────────────────────────────────────────────────────

export interface AdminPartnerOut {
  id: string;
  user_id: string;
  status: string;
  contact_method: string | null;
  contact_detail: string | null;
  reject_reason: string | null;
  applied_at: string | null;
  decided_at: string | null;
  decided_by: string | null;
}

export const adminListPartners = (email: string, status?: string) =>
  adminApi(email).get<AdminPartnerOut[]>('/api/v1/admin/partners', {
    params: status ? { status } : undefined,
  });

export const adminApprovePartner = (email: string, userId: string) =>
  adminApi(email).post<AdminPartnerOut>(`/api/v1/admin/partners/${userId}/approve`);

export const adminRejectPartner = (email: string, userId: string, reason: string) =>
  adminApi(email).post<AdminPartnerOut>(`/api/v1/admin/partners/${userId}/reject`, { reason });

// ── Customer support ────────────────────────────────────────────────────────

export interface SupportMessageT {
  id: string;
  author: 'user' | 'admin';
  author_email?: string | null;
  body: string;
  created_at?: string | null;
}

export interface SupportTicketT {
  id: string;
  category: string;
  subject: string;
  status: string;
  last_reply_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  messages?: SupportMessageT[];
  user_email?: string;
}

export const SUPPORT_CATEGORIES = [
  'general', 'deposit', 'withdrawal', 'bets', 'account', 'kyc',
  'responsible_gambling', 'complaint',
] as const;

export const getSupportContact = () =>
  api.get<{ email: string; whatsapp_number: string | null }>('/api/v1/support/contact');

export const createSupportTicket = (category: string, subject: string, body: string) =>
  api.post<SupportTicketT>('/api/v1/support/tickets', { category, subject, body });

export const listSupportTickets = () =>
  api.get<SupportTicketT[]>('/api/v1/support/tickets');

export const getSupportTicket = (id: string) =>
  api.get<SupportTicketT>(`/api/v1/support/tickets/${id}`);

export const replySupportTicket = (id: string, body: string) =>
  api.post<SupportMessageT>(`/api/v1/support/tickets/${id}/messages`, { body });

// Admin support
export const adminListSupportTickets = (email: string, status?: string) =>
  adminApi(email).get<SupportTicketT[]>(
    `/api/v1/admin/support/tickets${status ? `?status=${status}` : ''}`,
  );

export const adminGetSupportTicket = (email: string, id: string) =>
  adminApi(email).get<SupportTicketT>(`/api/v1/admin/support/tickets/${id}`);

export const adminReplySupportTicket = (email: string, id: string, body: string) =>
  adminApi(email).post<SupportMessageT>(`/api/v1/admin/support/tickets/${id}/reply`, { body });

export const adminSetSupportStatus = (email: string, id: string, status: string) =>
  adminApi(email).patch<SupportTicketT>(`/api/v1/admin/support/tickets/${id}/status`, { status });

export default api;
